import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { EmptyBasketState as EmptyBasketSchema } from '@amplience/content-types/typings/c-empty-basket';
import { useLocaleContext } from '../../../context/localeContext';
import { useOverlayContext } from '../../../context/overlayContext';
import { TypographyStylesType, colours } from '../../../stylings';
import { createAnchorTagHref, getLocalizedValue } from '../../../utils/transformers';
import { Button, Paragraph, TextBlock, Title, Wrapper } from './EmptyBasket.styles';

export type EmptyBasketProps = EmptyBasketSchema & {
  className?: string;
};

export function EmptyBasket({ title, copyBlock, buttonText, className }: EmptyBasketProps) {
  const { t } = useTranslation(['lib-global-common']);
  const { isMiniCartOpen, setIsMiniCartOpen } = useOverlayContext();
  const { locale } = useLocaleContext();
  const router = useRouter();

  const onContinueShopping = () => {
    const goToHomepage = window.history.length > 1 && document.referrer.includes('checkout');

    if (isMiniCartOpen) {
      setIsMiniCartOpen(false);
      return;
    }

    if (goToHomepage) {
      router.push({ pathname: createAnchorTagHref({ locale, path: '/' }) });
      return;
    }

    router.back();
  };

  return (
    <Wrapper className={className}>
      <TextBlock data-testid="emptyCartText" role="status">
        <Title {...title} defaultTextSize={TypographyStylesType.BODY_SMALL_SEMIBOLD} defaultTextColor={colours.BLACK}>
          {getLocalizedValue(title?.text) ?? t('cart.empty.title')}
        </Title>
        {copyBlock?.text && (
          <Paragraph
            {...copyBlock}
            defaultTextSize={TypographyStylesType.BODY_SMALL_REGULAR}
            defaultTextColor={colours.BLACK}
          >
            {getLocalizedValue(copyBlock?.text)}
          </Paragraph>
        )}
      </TextBlock>
      <Button type="button" onClick={onContinueShopping}>
        {getLocalizedValue(buttonText) ?? t('cart.continue.shopping')}
      </Button>
    </Wrapper>
  );
}
